@file:Suppress("MagicNumber")

package top.wcpe.allincore.ui.runtime.element

import kotlin.jvm.JvmStatic

/**
 * 模态弹层元素。
 *
 * 由 `ModalLayer` 在普通元素树渲染之后单独渲染：先铺一层全屏 backdrop
 * 颜色矩形，再绘制 modal 自身子树。[UIElement.visible] 控制显示与否：
 * 不可见时既不渲染也不响应事件，子树被整体短路。
 *
 * 当前实现的简化点（M6-h Round 1）：
 * - visible=false 仍构建 UIElement 实例，仅跳过渲染/命中（RFC §9.1 描述的"实例化但不挂载"
 *   将随 ModalLayer 优化在后续 round 落地）。
 * - 淡入淡出动画留给 M6-i 反应式动画统一实现。
 * - `dismissOnEsc` 字段已记录，但键盘 ESC 关闭由 Round 2 与键盘事件链路一起接入。
 */
class Modal private constructor() : UIElement<Modal>() {

    // ===== 行为开关 =====
    private var dismissOnOutsideClickValue: Boolean = true
    private var dismissOnEscValue: Boolean = true
    private var centeredValue: Boolean = true
    private var fullscreenValue: Boolean = false
    private var passthroughValue: Boolean = false
    private var offsetXValue: Float = 0f
    private var offsetYValue: Float = 0f
    private var backdropValue: Int = DEFAULT_BACKDROP
    private var fadeInMsValue: Long = FADE_IN_MS

    // ===== spotlight =====
    private var spotlightTargetIdValue: String? = null
    private var spotlightPaddingValue: Float = 4f
    private var spotlightDimColorValue: Int = 0xC0000000.toInt()

    // ===== anchor 定位 =====
    private var anchorTargetIdValue: String? = null
    private var anchorPositionValue: String = "below"
    private var anchorAlignValue: String = "center"
    private var anchorGapValue: Float = 8f

    // ===== 状态管理 =====
    private var exitingValue: Boolean = false
    private var onDismissActionNameValue: String? = null
    private var enterStartMsValue: Long = 0L
    private var lastVisibleStateValue: Boolean = false
    private var lastRenderedOriginValue: FloatArray? = null

    init {
        // modal 默认不可见，需 visible=true 才进入 ModalLayer 渲染（与 RFC §9.1 默认值一致）
        this.visibleField = false
    }

    // ===== CRTP 自引用 =====
    override fun self(): Modal = this

    /**
     * Modal 是绝对浮层，永远不参与父容器的 flex / column 排版——
     * 即便 visible=true，也不应挤压父节点其它子项。布局引擎会把 modal 子树
     * 作为独立 layout root 单独计算，`ModalLayer` 通过 `layout.boxOf(modal)` 仍能拿到自身宽高做居中。
     */
    override fun detachedFromParentLayout(): Boolean = true

    // ===== 行为开关 =====
    fun dismissOnOutsideClick(v: Boolean): Modal {
        dismissOnOutsideClickValue = v
        return this
    }
    fun dismissOnOutsideClick(): Boolean = dismissOnOutsideClickValue
    fun dismissOnEsc(v: Boolean): Modal {
        dismissOnEscValue = v
        return this
    }
    fun dismissOnEsc(): Boolean = dismissOnEscValue
    fun centered(v: Boolean): Modal {
        centeredValue = v
        return this
    }
    fun centered(): Boolean = centeredValue

    /**
     * 全屏模式：modal 子树以整个屏幕（screenWidth × screenHeight）作为渲染画布，
     * 而非按 modal 自身宽高居中。
     *
     * fullscreen=true 时，centered/offsetX/offsetY 都被忽略，
     * ModalLayer 直接从屏幕 (0,0) 起按屏幕宽高渲染。
     */
    fun fullscreen(v: Boolean): Modal {
        fullscreenValue = v
        return this
    }
    fun fullscreen(): Boolean = fullscreenValue

    /**
     * 命中穿透：当鼠标点击落在 modal 子树未命中任何子节点的区域时，
     * 不"吸收"事件，而是回落到主元素树继续命中测试。
     *
     * passthrough=true 时 dismissOnOutsideClick 被忽略——两者语义互斥。
     */
    fun passthrough(v: Boolean): Modal {
        passthroughValue = v
        return this
    }
    fun passthrough(): Boolean = passthroughValue

    fun offsetX(v: Float): Modal {
        offsetXValue = v
        return this
    }
    fun offsetX(): Float = offsetXValue
    fun offsetY(v: Float): Modal {
        offsetYValue = v
        return this
    }
    fun offsetY(): Float = offsetYValue
    fun backdrop(color: Int): Modal {
        backdropValue = color
        return this
    }
    fun backdrop(): Int = backdropValue
    fun fadeInMs(ms: Long): Modal {
        fadeInMsValue = maxOf(0L, ms)
        return this
    }
    fun fadeInMs(): Long = fadeInMsValue

    // ===== spotlight =====
    fun spotlightTargetId(id: String?): Modal {
        spotlightTargetIdValue = id
        return this
    }
    fun spotlightTargetId(): String? = spotlightTargetIdValue
    fun spotlightPadding(v: Float): Modal {
        spotlightPaddingValue = v
        return this
    }
    fun spotlightPadding(): Float = spotlightPaddingValue
    fun spotlightDimColor(color: Int): Modal {
        spotlightDimColorValue = color
        return this
    }
    fun spotlightDimColor(): Int = spotlightDimColorValue

    // ===== anchor 定位 =====
    fun anchorTargetId(id: String?): Modal {
        anchorTargetIdValue = id
        return this
    }
    fun anchorTargetId(): String? = anchorTargetIdValue
    fun anchorPosition(pos: String): Modal {
        anchorPositionValue = pos
        return this
    }
    fun anchorPosition(): String = anchorPositionValue
    fun anchorAlign(align: String): Modal {
        anchorAlignValue = align
        return this
    }
    fun anchorAlign(): String = anchorAlignValue
    fun anchorGap(gap: Float): Modal {
        anchorGapValue = gap
        return this
    }
    fun anchorGap(): Float = anchorGapValue

    // ===== 状态管理 =====
    fun exiting(): Boolean = exitingValue
    fun exiting(v: Boolean) {
        exitingValue = v
    }
    fun zIndex(z: Double): Modal {
        super.zIndex(z.toInt())
        return this
    }
    fun onDismissActionName(name: String?): Modal {
        onDismissActionNameValue = name
        return this
    }
    fun onDismissActionName(): String? = onDismissActionNameValue

    /** 标记本次淡入起点（毫秒），由 ModalLayer 在 visible 上升沿调用。 */
    fun markEnterStart(nowMs: Long) {
        enterStartMsValue = nowMs
        lastVisibleStateValue = true
    }

    /** 重置上升沿状态。visible 转为 false 时调用，便于下次再次淡入。 */
    fun resetEnterState() {
        lastVisibleStateValue = false
    }
    fun enterStartMs(): Long = enterStartMsValue
    fun lastVisibleState(): Boolean = lastVisibleStateValue

    /**
     * 写入本帧实际渲染使用的屏幕坐标 origin（含尺寸）。
     * 由 ModalLayer.renderOne 在 computeModalOrigin 算出 origin 后调用，
     * 使命中测试能读到与渲染一致的位置 + 尺寸。
     */
    fun lastRenderedOrigin(x: Float, y: Float, w: Float, h: Float) {
        val arr = lastRenderedOriginValue ?: FloatArray(4).also { lastRenderedOriginValue = it }
        arr[0] = x
        arr[1] = y
        arr[2] = w
        arr[3] = h
    }

    /** 兼容旧 2 参签名：仅写 (x, y)，宽高保持上一次缓存值或 0。 */
    fun lastRenderedOrigin(x: Float, y: Float) {
        val arr = lastRenderedOriginValue ?: FloatArray(4).also { lastRenderedOriginValue = it }
        arr[0] = x
        arr[1] = y
    }

    /**
     * 读取上一帧渲染 origin。返回 4 元素数组 [x, y, w, h]；未渲染过时返回 null，
     * 命中侧应退回 layout box 坐标。
     */
    fun lastRenderedOrigin(): FloatArray? = lastRenderedOriginValue

    /** 清除渲染 origin 缓存。visible 转为 false 时由 ModalLayer 调用。 */
    fun clearLastRenderedOrigin() {
        lastRenderedOriginValue = null
    }

    companion object {
        /** 默认背景遮罩颜色：50% 透明黑（RFC §9.1）。 */
        const val DEFAULT_BACKDROP: Int = 0x80000000.toInt()

        /** 默认层级：高于普通元素与 dropdown overlay（RFC §9.1）。 */
        const val DEFAULT_Z_INDEX: Double = 1000.0

        /** 默认淡入时长（毫秒）。 */
        const val FADE_IN_MS: Long = 150L

        /** 创建新的模态弹层元素。 */
        @JvmStatic
        fun create(): Modal = Modal()
    }
}
